import { Box, Fade, Paper, Skeleton, Typography } from '@mui/material'
import { BarChart, LineChart } from '@mui/x-charts'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'

import { useAppSelector } from 'src/store/hooks'
import { BarChart as ChartIcon } from '@mui/icons-material'
import {
  selectActiveChartType,
  selectChartData,
  selectCategories,
  selectIsLoadingData,
} from '../../index'

export default function AnalyticsView() {
  // Redux selectors
  let activeChartType = useAppSelector(selectActiveChartType)
  let chartData = useAppSelector(selectChartData)
  let categories = useAppSelector(selectCategories)
  let isLoadingData = useAppSelector(selectIsLoadingData)

  let renderChart = () => {
    switch (activeChartType) {
      case 'license':
      case 'group':
        if (chartData.length === 0) {
          let emptyMessage =
            activeChartType === 'license'
              ? 'No license type data available for chart'
              : 'No group data available for chart'
          return (
            <Box
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography>{emptyMessage}</Typography>
            </Box>
          )
        }

        try {
          // Create series for each category (stacked bars) with default MUI colors
          let series = categories.map((category) => ({
            dataKey: category,
            label: category,
            stack: 'categories',
            valueFormatter: nullSafeValueFormatter,
          }))

          let xAxisLabel =
            activeChartType === 'license' ? 'License Types' : 'Groups'
          let dataKey =
            activeChartType === 'license' ? 'licenseType' : 'groupName'

          return (
            <BarChart
              dataset={chartData}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey,
                  label: xAxisLabel,
                },
              ]}
              yAxis={[
                {
                  label: 'CE Hours',
                },
              ]}
              series={series}
              height={400}
              margin={{ left: 80, right: 20, top: 20, bottom: 100 }}
            />
          )
        } catch (error) {
          return (
            <Box
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main',
              }}
            >
              <Typography>
                Error rendering chart:{' '}
                {(error as Error)?.message || 'Unknown error'}
              </Typography>
            </Box>
          )
        }

      case 'user':
        if (chartData.length === 0) {
          return (
            <Box
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography>
                No time series data available. Please select users to view their
                CE trends over time.
              </Typography>
            </Box>
          )
        }

        try {
          // Create series for each category (multiple lines) with default MUI colors
          let series = categories.map((category) => ({
            dataKey: category,
            label: category,
            curve: 'linear' as const,
            valueFormatter: nullSafeValueFormatter,
          }))

          return (
            <LineChart
              dataset={chartData}
              xAxis={[
                {
                  scaleType: 'band' as const,
                  dataKey: 'timeFrame',
                  label: 'Time Period',
                },
              ]}
              yAxis={[
                {
                  label: 'CE Hours',
                },
              ]}
              series={series}
              height={400}
              margin={{ left: 80, right: 20, top: 20, bottom: 80 }}
            />
          )
        } catch (error) {
          return (
            <Box
              sx={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'error.main',
              }}
            >
              <Typography>
                Error rendering line chart:{' '}
                {(error as Error)?.message || 'Unknown error'}
              </Typography>
            </Box>
          )
        }

      default:
        return null
    }
  }

  let getChartTitle = () => {
    switch (activeChartType) {
      case 'license':
        return 'CE Hours by License Type & Category'
      case 'user':
        return 'CE Hours Over Time by Category (Selected Users)'
      case 'group':
        return 'CE Hours by Group & Category'
      default:
        return 'Advanced Analytics'
    }
  }

  let getChartDescription = () => {
    switch (activeChartType) {
      case 'license':
        return 'Stacked bar chart showing CE hours for each license type, broken down by CE categories. Users with multiple licenses are counted per license.'
      case 'user':
        return 'Time series showing how CE hours in each category have changed over months for the selected users'
      case 'group':
        return 'Stacked bar chart showing CE hours for each selected group, broken down by CE categories'
      default:
        return 'Interactive charts showing CE activity patterns and trends'
    }
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <ChartIcon />
          {getChartTitle()}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 600 }}
        >
          {getChartDescription()}
        </Typography>
      </Box>
      {isLoadingData ? (
        <Skeleton variant="rectangular" height={400} />
      ) : chartData.length === 0 ? (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <ChartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Data Available
          </Typography>
          <Typography variant="body2">
            No CE activities found for the selected criteria. Try adjusting your
            filters or date range.
          </Typography>
        </Box>
      ) : (
        <Fade in={!isLoadingData} timeout={1000}>
          <Box>{renderChart()}</Box>
        </Fade>
      )}
    </Paper>
  )
}
